import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CircleIcon from '@mui/icons-material/Circle';
import ClearIcon from '@mui/icons-material/Clear';
import DoneIcon from '@mui/icons-material/Done';
import { Button, Container, Modal, Stack, Step, StepButton, Stepper, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  AssessmentSteps,
  markAssessmentDone,
  selectAssessmentDone,
  selectCurrentStep,
  selectEvaluationComplete,
  selectSelectedAssessment,
} from '../../redux/reducers/assessmentReducer';

function AssessmentPhases({ handleStepChange }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const current = useSelector(selectCurrentStep);
  const steps = [AssessmentSteps.PLAN, AssessmentSteps.EVALUATION, AssessmentSteps.REVIEW, AssessmentSteps.DONE];
  const assessment = useSelector(selectSelectedAssessment);
  const evaluationComplete = useSelector(selectEvaluationComplete);
  const assessmentDone = useSelector(selectAssessmentDone);
  const theme = useTheme();

  const [modalOpen, setModalOpen] = useState(false);
  const handleOpenModal = () => {
    setModalOpen(true);
  };
  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleMarkAsDone = () => {
    dispatch(markAssessmentDone());
    handleStepChange(AssessmentSteps.DONE);
  };

  function renderButtons(step) {
    switch (step) {
      case AssessmentSteps.DONE:
        return false;
      case AssessmentSteps.REVIEW:
        return (
          <Stack direction="row" justifyContent="space-between">
            <Button
              id='back-to-review'
              size="small"
              variant="contained"
              startIcon={<ChevronLeftIcon />}
              onClick={() => handleStepChange(AssessmentSteps.EVALUATION)}
            >
              {t('assessment.phases.buttonBackToEvaluation')}
            </Button>
            <Button
              id='mark-as-done'
              size="small"
              variant="contained"
              disabled={!evaluationComplete}
              endIcon={<DoneIcon />}
              onClick={handleOpenModal}
            >
              {t('assessment.phases.buttonMarkAsDone')}
            </Button>
            <Modal open={modalOpen} onClose={handleCloseModal}>
              <Container variant="modal" disableGutters sx={{ width: '600px' }}>
                <Stack variant="modalTop" spacing={2}>
                  <Typography variant="h6">{t('assessment.done.modalTitle')}</Typography>
                  <Typography>{t('assessment.done.modalDescription')}</Typography>
                  <Typography>{t('assessment.done.modalWarning')}</Typography>
                </Stack>
                <Stack direction="row" variant="modalButtons" spacing={1}>
                  <Button id='cancel' variant="text" startIcon={<ClearIcon />} onClick={handleCloseModal}>
                    {t('assessment.done.modalCancel')}
                  </Button>
                  <Button id='confirm-done' variant="contained" startIcon={<DoneIcon />} onClick={() => handleMarkAsDone()}>
                    {t('assessment.done.modalAccept')}
                  </Button>
                </Stack>
              </Container>
            </Modal>
          </Stack>
        );
      case AssessmentSteps.EVALUATION:
        return (
          <Stack direction="row" spacing={1.25}>
            <Button
              id='back-to-plan'
              size="small"
              variant="contained"
              startIcon={<ChevronLeftIcon />}
              onClick={() => handleStepChange(AssessmentSteps.PLAN)}
            >
              {t('assessment.phases.buttonBackToPlan')}
            </Button>
            <Button
              id='move-to-review'
              size="small"
              variant="contained"
              endIcon={<ChevronRightIcon />}
              onClick={() => handleStepChange(AssessmentSteps.REVIEW)}
            >
              {t('assessment.phases.buttonToReview')}
            </Button>
          </Stack>
        );
      case AssessmentSteps.PLAN:
      default:
        return (
          <Stack direction="row">
            <Button
              id='move-to-evaluation'
              size="small"
              variant="contained"
              endIcon={<ChevronRightIcon />}
              onClick={() => handleStepChange(AssessmentSteps.EVALUATION)}
            >
              {t('assessment.phases.buttonToEvaluation')}
            </Button>
          </Stack>
        );
    }
  }

  function isFirstStep(step) {
    return steps.findIndex((s) => s === step) === 0;
  }

  function isLastStep(step) {
    return steps.findIndex((s) => s === step) === steps.length - 1;
  }

  function getSx(step) {
    if (isFirstStep(step)) {
      return { pl: 0 };
    }
    if (isLastStep(step)) {
      return { pr: 0 };
    }
    return {};
  }

  return assessmentDone ? (
    false
  ) : (
    <Stack variant="assessmentPhases" spacing={2}>
      <Stepper nonLinear activeStep={steps.findIndex((step) => step === current)}>
        {steps.map((step) => (
          <Step key={step} sx={getSx(step)}>
            <StepButton
              color="inherit"
              icon={<CircleIcon htmlColor={step === current ? theme.palette.steps.dark : theme.palette.steps.light} />}
              onClick={!assessment.done && step !== AssessmentSteps.DONE ? () => handleStepChange(step) : undefined}
              disabled={assessment.done}
            >
              {t(`assessment.phases.${step}`)}
            </StepButton>
          </Step>
        ))}
      </Stepper>
      {renderButtons(current)}
    </Stack>
  );
}
export default AssessmentPhases;
